import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSelector } from "@/components/LanguageSelector";
import { GradientButton } from "@/components/common";
import { useI18n } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import { useCtaActions } from "@/lib/ctaActions";
import { useAuth } from "@/saas/AuthContext";
import { cn } from "@/lib/utils";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const navLinks = t("nav.links");
  const { goLogin, goRegister, goDashboard } = useCtaActions();
  const { user, org } = useAuth();
  const isCustomer = user && (user.role === "AGENCY_ADMIN" || user.role === "AGENCY_USER");
  const isOwner = user && user.role === "OWNER_ADMIN";
  const loggedIn = isCustomer || isOwner;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300",
          scrolled
            ? "border-b border-re-line/80 bg-white/80 shadow-[0_4px_20px_-8px_rgba(13,13,15,0.12)] backdrop-blur-xl"
            : "border-b border-transparent bg-white"
        )}
      >
        <nav className="container-re flex h-16 items-center justify-between md:h-[72px]">
          <a href="/" aria-label={t("nav.ariaHome")} data-testid="nav-logo"
             onClick={(e) => { e.preventDefault(); window.location.assign("/"); }}>
            <Logo variant="light" emblemY={7} />
          </a>

          <div className="hidden items-center gap-8 lg:flex">
            {navLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm font-medium text-re-charcoal transition-colors hover:text-re-red"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="hidden items-center gap-5 lg:flex">
            <LanguageSelector />
            {loggedIn ? (
              <>
                {isCustomer && org?.name && (
                  <span className="max-w-[180px] truncate text-sm font-medium text-re-muted" data-testid="nav-org-name">{org.name}</span>
                )}
                <GradientButton testid="nav-dashboard" onClick={goDashboard} className="px-5 py-2.5">
                  {t("nav.dashboard")} <ArrowRight className="h-4 w-4" />
                </GradientButton>
              </>
            ) : (
              <>
                <button
                  onClick={goLogin}
                  data-testid="nav-login"
                  className="text-sm font-semibold text-re-ink transition-colors hover:text-re-red"
                >
                  {t("nav.login")}
                </button>
                <GradientButton testid="nav-cta" onClick={() => goRegister()} className="px-5 py-2.5">
                  {t("nav.start")} <ArrowRight className="h-4 w-4" />
                </GradientButton>
              </>
            )}
          </div>

          <button
            className="text-re-ink lg:hidden"
            onClick={() => setOpen(true)}
            aria-label={t("nav.ariaMenuOpen")}
            data-testid="mobile-menu-open"
          >
            <Menu className="h-6 w-6" />
          </button>
        </nav>
      </motion.header>

      {/* Mobile slide-out */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="fixed right-0 top-0 z-[70] flex h-full w-[82%] max-w-sm flex-col bg-re-ink px-6 py-6 lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between">
                <Logo variant="dark" />
                <button onClick={() => setOpen(false)} aria-label={t("nav.ariaMenuClose")} data-testid="mobile-menu-close" className="text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-10 flex flex-col gap-1">
                {navLinks.map((l, i) => (
                  <motion.button
                    key={l.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06 }}
                    onClick={() => go(l.id)}
                    data-testid={`mobile-link-${l.id}`}
                    className="border-b border-white/10 py-4 text-left font-heading text-xl font-bold text-white"
                  >
                    {l.label}
                  </motion.button>
                ))}
              </div>
              <div className="mt-auto flex flex-col gap-3">
                <LanguageSelector variant="dark" className="self-start" />
                {loggedIn ? (
                  <GradientButton testid="mobile-dashboard" onClick={() => { setOpen(false); goDashboard(); }} className="w-full">
                    {t("nav.dashboard")}
                  </GradientButton>
                ) : (
                  <>
                    <button onClick={() => { setOpen(false); goLogin(); }} data-testid="mobile-login" className="text-sm font-semibold text-white/70">
                      {t("nav.login")}
                    </button>
                    <GradientButton testid="mobile-cta" onClick={() => { setOpen(false); goRegister(); }} className="w-full">
                      {t("nav.start")}
                    </GradientButton>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
